import { AloftPrice, CompType, schAlertType, schCompType } from '@aloft/toolkit-shared';

import { OrderState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getDepreciatedCostPageRoute } from '../utils/getDepreciatedCostPageRoute.ts';

const getNegativeSiteValuePropertyName = ({
  propertyID,
  compSequence,
  fullCompSequence,
  improvementEntityIds,
}: {
  propertyID: string;
  compSequence: string[];
  fullCompSequence?: string[];
  improvementEntityIds: string[];
}) => {
  if (propertyID === 'subject') {
    return improvementEntityIds.includes('subjectArv') ? 'Subject AIV' : 'Subject';
  }
  if (propertyID === 'subjectArv') {
    return 'Subject ARV';
  }
  const sequence = fullCompSequence ?? compSequence;
  return `Comparable ${sequence.findIndex((id) => id === propertyID) + 1}`;
};

const getNegativeSiteValueCompType = (
  propertyID: string,
  improvementEntityIds: string[],
  order: OrderState,
): CompType | undefined => {
  if (!order.hasArvOption) {
    return undefined;
  }
  if (propertyID === 'subjectArv') {
    return schCompType.enum.arv;
  }
  if (propertyID === 'subject') {
    return improvementEntityIds.includes('subjectArv') ? schCompType.enum.aiv : undefined;
  }
  return order.compTypes?.[propertyID] ?? schCompType.enum.aiv;
};

const getSiteValuesToCheck = (
  siteValues: Record<string, AloftPrice | undefined>,
  improvementEntityIds: string[],
) => {
  const entries = Object.entries(siteValues);
  if (improvementEntityIds.includes('subjectArv') && siteValues.subject) {
    return [...entries, ['subjectArv', siteValues.subject] as const];
  }
  return entries;
};

export const negativeSiteValue: AlertDefinition = ({
  compSequence,
  fullCompSequence,
  improvementEntityIds,
  order,
  siteValues,
}) => {
  const negativeSiteValues = getSiteValuesToCheck(siteValues, improvementEntityIds).filter(
    (entry): entry is [string, AloftPrice] =>
      entry[1] !== undefined && entry[1].lt(AloftPrice.zero),
  );

  if (negativeSiteValues.length === 0) return [];

  const fixRoute = getDepreciatedCostPageRoute(order);

  return negativeSiteValues.map(([propertyID, siteValue]) => {
    const propertyName = getNegativeSiteValuePropertyName({
      propertyID,
      compSequence,
      fullCompSequence,
      improvementEntityIds,
    });
    const compType = getNegativeSiteValueCompType(propertyID, improvementEntityIds, order);

    return {
      type: schAlertType.enum.negativeSiteValue,
      status: 'active' as const,
      severity: 'error' as const,
      orderType: ['saas', 'appraisify'] as const,
      message: `${propertyName} has a negative Site Value (${siteValue.toString()}).`,
      propertyID,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: 'Tips: Adjust the Cost Quality or Effective Age sliders to accurately reflect the observed construction quality and remaining utility of the property,',
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['action'] as const,
        commentAlwaysRequired: false,
      },
      triggers: getAlertTriggers({ fixRoute, order }),
    };
  });
};
